import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid2 as Grid,
  Chip,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  IconButton,
  Avatar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  CircularProgress,
  Alert,
  Switch,
  Tooltip,
  FormControlLabel,
  Link,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Store as StoreIcon,
  CheckCircle as ActiveIcon,
  Cancel as InactiveIcon,
  Refresh as RefreshIcon,
  Settings as SettingsIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import shopService, { Store, StoreStats, StoreType } from '../../services/shop.service';
import api from '../../services/api';
import { useNavigate } from 'react-router-dom';

/** Estado da loja no Chat-to-Pay: sem registo = por configurar */
interface CanalChat { no_canal: boolean; activo: boolean }
/** Mudança de canal à espera de confirmação */
interface MudarCanal { store: Store; canal: 'SHOP' | 'CHAT'; ligar: boolean }

const Stores: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [stores, setStores] = useState<Store[]>([]);
  // Canais (29/10/2026): montra do Pagali Shop e Chat-to-Pay, cada um com o seu interruptor
  const [chat, setChat] = useState<Record<string, CanalChat>>({});
  const [mudarCanal, setMudarCanal] = useState<MudarCanal | null>(null);
  const [canalBusy, setCanalBusy] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [needsSetup, setNeedsSetup] = useState(false);
  const [settingUp, setSettingUp] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [total, setTotal] = useState(0);
  const [editDialog, setEditDialog] = useState(false);
  const [selectedStore, setSelectedStore] = useState<Store | null>(null);
  const [saving, setSaving] = useState(false);
  const [storeTypes, setStoreTypes] = useState<StoreType[]>([]);
  const [entities, setEntities] = useState<{ id: string; nome: string }[]>([]);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    logo: '',
    address: '',
    phone: '',
    schedule: '',
    typeId: '',
    entityId: '',
    status: 'active' as 'active' | 'inactive' | 'draft',
  });
  const [stats, setStats] = useState<StoreStats>({
    total: 0,
    active: 0,
  });

  const getStatusChip = (status: string) => {
    if (status === 'active') return <Chip size="small" color="success" label={t('shop.active')} icon={<ActiveIcon />} />;
    if (status === 'draft') return <Chip size="small" color="warning" label="Rascunho" />;
    return <Chip size="small" color="default" label={t('shop.inactive')} icon={<InactiveIcon />} />;
  };

  const fetchStores = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await shopService.getStores({
        page: page + 1,
        limit: rowsPerPage,
      });

      if (response.success) {
        setStores(response.data || []);
        const coms = await api.get('/admin/chat-to-pay/comerciantes').catch(() => null);
        const porLoja: Record<string, CanalChat> = {};
        for (const c of coms?.data?.data || []) if (!c.food) porLoja[c.loja_id] = { no_canal: !!c.no_canal, activo: !!c.activo };
        setChat(porLoja);
        setTotal(response.pagination?.total || 0);
        if (response.needsSetup) {
          setNeedsSetup(true);
        } else {
          setNeedsSetup(false);
        }
      }
    } catch (err: any) {
      console.error('Error fetching stores:', err);
      setError(t('shop.errorLoadingStores'));
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage]);

  const aplicarCanal = async () => {
    if (!mudarCanal) return;
    const { store, canal, ligar } = mudarCanal;
    setCanalBusy(true);
    try {
      if (canal === 'SHOP') await api.put(`/admin/shop/stores/${store.id}`, { montraVisivel: ligar });
      else await api.put(`/admin/chat-to-pay/comerciantes/${encodeURIComponent(store.id)}`, { activo: ligar });
      setSuccessMessage(t('shop.canais.feito', {
        loja: store.name,
        canal: canal === 'SHOP' ? t('shop.canais.shop') : t('shop.canais.chat'),
        estado: ligar ? t('shop.canais.ligado') : t('shop.canais.desligado'),
      }));
      fetchStores();
    } catch (err: any) {
      setError(err?.response?.data?.error || t('shop.canais.erro'));
    }
    setMudarCanal(null);
    setCanalBusy(false);
  };

  const fetchStats = useCallback(async () => {
    try {
      const response = await shopService.getStoreStats();
      if (response.success) {
        setStats(response.data);
      }
    } catch (err) {
      console.error('Error fetching stats:', err);
    }
  }, []);

  const fetchStoreTypes = useCallback(async () => {
    try {
      const response = await shopService.getStoreTypes();
      if (response.success) {
        setStoreTypes(response.data || []);
      }
    } catch (err) {
      console.error('Error fetching store types:', err);
    }
  }, []);

  const fetchEntities = useCallback(async () => {
    try {
      const response = await api.get('/admin/entities?limit=200');
      if (response.data.success) {
        setEntities(response.data.data?.map((e: any) => ({ id: e.id, nome: e.nome || e.desig || e.name })) || []);
      }
    } catch (err) {
      console.error('Error fetching entities:', err);
    }
  }, []);

  useEffect(() => {
    fetchStores();
    fetchStats();
    fetchStoreTypes();
    fetchEntities();
  }, [fetchStores, fetchStats, fetchStoreTypes, fetchEntities]);

  const handleEntitySelect = useCallback(async (entityId: string, currentLogo: string) => {
    setFormData(prev => ({ ...prev, entityId }));
    if (!entityId || currentLogo) return;
    try {
      const res = await api.get(`/admin/entities/${entityId}`);
      const entity = res.data?.data;
      if (entity?.logo) {
        const logoData = entity.logo;
        let dataUrl = '';
        if (typeof logoData === 'object' && logoData.type === 'Buffer' && Array.isArray(logoData.data)) {
          const bytes = new Uint8Array(logoData.data);
          const binary = bytes.reduce((acc: string, b: number) => acc + String.fromCharCode(b), '');
          dataUrl = `data:${entity.mimeType || 'image/png'};base64,${btoa(binary)}`;
        } else if (typeof logoData === 'string') {
          dataUrl = logoData.startsWith('data:') ? logoData : `data:${entity.mimeType || 'image/png'};base64,${logoData}`;
        }
        if (dataUrl) setFormData(prev => ({ ...prev, logo: dataUrl }));
      }
    } catch { /* ignore — logo not critical */ }
  }, []);

  const handleOpenEdit = (store?: Store) => {
    if (store) {
      setSelectedStore(store);
      setFormData({
        name: store.name,
        description: store.description || '',
        logo: store.logo || '',
        address: store.address || '',
        phone: store.phone || '',
        schedule: store.schedule || '',
        typeId: store.typeId || '',
        entityId: store.entityId || '',
        status: store.status,
      });
    } else {
      setSelectedStore(null);
      setFormData({
        name: '',
        description: '',
        logo: '',
        address: '',
        phone: '',
        schedule: '',
        typeId: '',
        entityId: '',
        status: 'active',
      });
    }
    setEditDialog(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      // Don't send base64 logo blobs back to the server (too large, already stored)
      const payload = { ...formData };
      if (payload.logo && payload.logo.startsWith('data:')) {
        delete (payload as any).logo;
      }

      if (selectedStore) {
        await shopService.updateStore(selectedStore.id, payload);
        setSuccessMessage(t('shop.storeUpdated'));
      } else {
        await shopService.createStore(payload);
        setSuccessMessage(t('shop.storeCreated'));
      }
      setEditDialog(false);
      setSelectedStore(null);
      fetchStores();
      fetchStats();
    } catch (err: any) {
      console.error('Error saving store:', err);
      setError(t('shop.errorSavingStore'));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (store: Store) => {
    if (window.confirm(t('shop.confirmDeleteStore', { name: store.name }))) {
      try {
        await shopService.deleteStore(store.id);
        setSuccessMessage(t('shop.storeDeleted'));
        fetchStores();
        fetchStats();
      } catch (err: any) {
        console.error('Error deleting store:', err);
        setError(t('shop.errorDeletingStore'));
      }
    }
  };

  const handleSetupTables = async () => {
    setSettingUp(true);
    setError(null);
    try {
      const response = await shopService.setupTables();
      if (response.success) {
        setSuccessMessage(response.message);
        setNeedsSetup(false);
        fetchStores();
        fetchStats();
        fetchStoreTypes();
      }
    } catch (err: any) {
      console.error('Error setting up tables:', err);
      setError(t('shop.errorSetup'));
    } finally {
      setSettingUp(false);
    }
  };

  const handleRefresh = () => {
    fetchStores();
    fetchStats();
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5" fontWeight={600}>
          {t('shop.stores')}
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button variant="outlined" startIcon={<RefreshIcon />} onClick={handleRefresh}>
            {t('common.refresh')}
          </Button>
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => handleOpenEdit()}>
            {t('shop.newStore')}
          </Button>
        </Box>
      </Box>

      {/* Alerts */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {successMessage && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccessMessage(null)}>
          {successMessage}
        </Alert>
      )}

      {/* Setup Alert */}
      {!loading && needsSetup && !error && (
        <Alert
          severity="warning"
          sx={{ mb: 3 }}
          action={
            <Button
              color="inherit"
              size="small"
              startIcon={settingUp ? <CircularProgress size={16} color="inherit" /> : <SettingsIcon />}
              onClick={handleSetupTables}
              disabled={settingUp}
            >
              {settingUp ? t('shop.settingUp') : t('shop.setupTables')}
            </Button>
          }
        >
          {t('shop.setupNeeded')}
        </Alert>
      )}

      {/* Stats */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid size={{ xs: 6 }}>
          <Card>
            <CardContent sx={{ py: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <StoreIcon color="primary" />
                <Box>
                  <Typography variant="h5" fontWeight={700}>
                    {stats.total}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {t('shop.totalStores')}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 6 }}>
          <Card>
            <CardContent sx={{ py: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <ActiveIcon color="success" />
                <Box>
                  <Typography variant="h5" fontWeight={700}>
                    {stats.active}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {t('shop.activeStores')}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Table */}
      <Paper>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell width={80}>{t('shop.imageHeader')}</TableCell>
                    <TableCell>{t('shop.designation')}</TableCell>
                    <TableCell>{t('shop.storeTypeHeader')}</TableCell>
                    <TableCell width={120}>{t('shop.statusHeader')}</TableCell>
                    <TableCell>{t('shop.canais.titulo')}</TableCell>
                    <TableCell width={120} align="center">
                      {t('shop.actionHeader')}
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {stores.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} sx={{ textAlign: 'center', py: 4 }}>
                        <Typography color="text.secondary">{t('shop.noStores')}</Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    stores.map((store) => (
                      <TableRow key={store.id} hover>
                        <TableCell>
                          <Avatar
                            src={store.logo}
                            sx={{
                              width: 40,
                              height: 40,
                              bgcolor: 'primary.main',
                            }}
                          >
                            {store.name.charAt(0)}
                          </Avatar>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" fontWeight={600}>
                            {store.name}
                          </Typography>
                          {store.description && (
                            <Typography variant="caption" color="text.secondary">
                              {store.description}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          <Chip size="small" label={store.typeName || 'N/A'} variant="outlined" />
                        </TableCell>
                        <TableCell>{getStatusChip(store.status)}</TableCell>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>
                          <Tooltip title={t('shop.canais.shopDica')}>
                            <FormControlLabel sx={{ display: 'flex', m: 0 }}
                              control={<Switch size="small" checked={Number(store.montraVisivel ?? 1) === 1}
                                onChange={(e) => setMudarCanal({ store, canal: 'SHOP', ligar: e.target.checked })} />}
                              label={<Typography variant="body2">{t('shop.canais.shop')}</Typography>} />
                          </Tooltip>
                          {chat[store.id]?.no_canal ? (
                            <Tooltip title={t('shop.canais.chatDica')}>
                              <FormControlLabel sx={{ display: 'flex', m: 0 }}
                                control={<Switch size="small" checked={chat[store.id].activo}
                                  onChange={(e) => setMudarCanal({ store, canal: 'CHAT', ligar: e.target.checked })} />}
                                label={<Typography variant="body2">{t('shop.canais.chat')}</Typography>} />
                            </Tooltip>
                          ) : (
                            <Tooltip title={t('shop.canais.configurarDica')}>
                              <Link component="button" variant="body2" underline="hover" sx={{ display: 'block', ml: 1, mt: 0.5 }}
                                onClick={() => navigate('/chat-to-pay')}>
                                {t('shop.canais.configurar')}
                              </Link>
                            </Tooltip>
                          )}
                        </TableCell>
                        <TableCell align="center">
                          <IconButton size="small" onClick={() => handleOpenEdit(store)}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                          <IconButton size="small" color="error" onClick={() => handleDelete(store)}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              component="div"
              count={total}
              page={page}
              onPageChange={(_, newPage) => setPage(newPage)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => {
                setRowsPerPage(parseInt(e.target.value, 10));
                setPage(0);
              }}
              rowsPerPageOptions={[10, 25, 50]}
              labelRowsPerPage={t('shop.rowsPerPage')}
              labelDisplayedRows={({ from, to, count }) => t('shop.paginationDisplay', { from, to, count })}
            />
          </>
        )}
      </Paper>

      {/* Edit Dialog */}
      <Dialog open={editDialog} onClose={() => setEditDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <StoreIcon />
          {selectedStore ? t('shop.editStore') : t('shop.newStore')}
        </DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2} sx={{ mt: 0 }}>
            <Grid size={{ xs: 12, md: 8 }}>
              <TextField
                fullWidth
                label={t('shop.designation')}
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <TextField
                fullWidth
                select
                label={t('shop.storeType')}
                value={formData.typeId}
                onChange={(e) => setFormData({ ...formData, typeId: e.target.value })}
              >
                <MenuItem value="">{t('shop.selectType')}</MenuItem>
                {storeTypes.map((type) => (
                  <MenuItem key={type.id} value={type.id}>
                    {type.name}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                required
                select
                label="Entidade *"
                value={formData.entityId}
                onChange={(e) => handleEntitySelect(e.target.value, formData.logo)}
              >
                <MenuItem value="">Seleccionar entidade</MenuItem>
                {entities.map((e) => (
                  <MenuItem key={e.id} value={e.id}>{e.nome}</MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                multiline
                rows={2}
                label={t('shop.descriptionLabel')}
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              {formData.logo && formData.logo.startsWith('data:') ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Avatar
                    src={formData.logo}
                    variant="rounded"
                    sx={{ width: 56, height: 56 }}
                  />
                  <Typography variant="caption" color="text.secondary">
                    {t('shop.currentLogo')}
                  </Typography>
                </Box>
              ) : (
                <TextField
                  fullWidth
                  label={t('shop.logoUrl')}
                  value={formData.logo}
                  onChange={(e) => setFormData({ ...formData, logo: e.target.value })}
                  placeholder="https://"
                />
              )}
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                select
                label={t('common.status')}
                value={formData.status}
                onChange={(e) => setFormData({ ...formData, status: e.target.value as 'active' | 'inactive' | 'draft' })}
              >
                <MenuItem value="active">{t('shop.active')}</MenuItem>
                <MenuItem value="draft">Rascunho</MenuItem>
                <MenuItem value="inactive">{t('shop.inactive')}</MenuItem>
              </TextField>
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                label={t('shop.addressLabel')}
                value={formData.address}
                onChange={(e) => setFormData({ ...formData, address: e.target.value })}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                label={t('common.phone')}
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                label={t('shop.schedule')}
                value={formData.schedule}
                onChange={(e) => setFormData({ ...formData, schedule: e.target.value })}
                placeholder="Ex: Seg-Sex 08:00-18:00"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialog(false)}>{t('common.cancel')}</Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={!formData.name || (!selectedStore && !formData.entityId) || saving}
            startIcon={saving ? <CircularProgress size={16} /> : null}
          >
            {saving ? t('common.saving') : selectedStore ? t('common.save') : t('common.create')}
          </Button>
        </DialogActions>
      </Dialog>


      <Dialog open={!!mudarCanal} onClose={() => setMudarCanal(null)} maxWidth="xs" fullWidth>
        <DialogTitle>
          {mudarCanal && t('shop.canais.pergunta', {
            acao: mudarCanal.ligar ? t('shop.canais.mostrar') : t('shop.canais.esconder'),
            loja: mudarCanal.store.name,
            canal: mudarCanal.canal === 'SHOP' ? t('shop.canais.shop') : t('shop.canais.chat'),
          })}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            {mudarCanal && t(`shop.canais.${mudarCanal.canal === 'SHOP' ? 'shop' : 'chat'}${mudarCanal.ligar ? 'Ligar' : 'Desligar'}`)}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMudarCanal(null)}>{t('common.cancel')}</Button>
          <Button variant="contained" color={mudarCanal?.ligar ? 'primary' : 'warning'} onClick={aplicarCanal} disabled={canalBusy}>
            {canalBusy ? <CircularProgress size={20} /> : mudarCanal?.ligar ? t('shop.canais.mostrar') : t('shop.canais.esconder')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Stores;
